/**
 * ZEN ERP — App Shell (Kök Bileşen)
 *
 * Sorumluluklar: kimlik doğrulama, tema/dil/animasyon tercihleri,
 * JSPM başlatma, context sağlayıcılar ve sayfa iskelet düzeni.
 *
 * Sekme routing mantığı → RouterShell.jsx
 * Her sayfa bileşeni    → pages/ veya components/
 */
import React, { useEffect, useState, useMemo } from "react";
import { Toaster, toast } from "sonner";

// Bildirimler 3 saniye sonra otomatik kapansın
const _origError = toast.error.bind(toast);
toast.error = (msg, opts) => _origError(msg, { duration: 3000, ...opts });

// ── Context & Utils ──────────────────────────────────────────────────────────
import {
  AuthCtx, UICtx, StockSettingsCtx, useStockSettingsProvider,
  NavigationCtx, RatesCtx, useRatesProvider,
} from "./appContexts";
import { setMoneyContext } from "./appUtils";
import { makeTr, applyStatusLang } from "./i18n";
import { translations } from "./mock";

// ── Layout ───────────────────────────────────────────────────────────────────
import LoginPage from "./pages/LoginPage";

import { Header, Sidebar } from "./components/layout/Sidebar";
import { RouterShell } from "./RouterShell";

// ── Tema ─────────────────────────────────────────────────────────────────────
import {
  BUILTIN_THEMES, generateThemeCSS,
  getCustomThemes, persistCustomThemes,
  getStoredActiveThemeId, setStoredActiveThemeId,
  getStoredAnimationsEnabled, setStoredAnimationsEnabled,
  getStoredCursorId, setStoredCursorId,
  getStoredClockStyle, setStoredClockStyle,
} from "./themeEngine";
import { AppearanceSettings, ThemeBackgroundFX } from "./themeSettings";

// ── Global Servisler & Overlay'lar ────────────────────────────────────────────
import api from "./api";
import { jspm } from "./lib/jspm";
import { AlarmChecker } from "./reminders";
import { useAppointmentAlarms } from "./pages/AppointmentsPage";
import { PaymentResultToast } from "./cardPayment";
import AIChatWidgetComponent from "./components/AIChatWidget";
import { Shortcuts } from "./Shortcuts";

import "./App.css";

// ─────────────────────────────────────────────────────────────────────────────
const DEALER_ALLOWED_TABS = ["dashboard", "orders", "quotes", "customers", "pricelists"];

export default function App() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme]     = useState(() => localStorage.getItem("zen_theme") || "dark");
  const [lang, setLang]       = useState("tr"); // Uygulama dili sabit: TR (çeviri kapsamı tamamlanınca EN seçeneği geri gelir)
  const [tab, _setTab]        = useState("dashboard");
  const [subTab, setSubTab]   = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem("zenSidebarOpen") === "true");

  // Randevu alarm sistemi
  useAppointmentAlarms(user);

  // ── Browser geri tuşu koruması ──────────────────────────────────────────────
  // Giriş yapınca history'e koruma kaydı ekle; back tuşu uygulamadan çıkmasın
  useEffect(() => {
    if (!user) return;
    // Koruma kaydı push et (henüz yoksa)
    if (!window.history.state?.zen_guard) {
      window.history.pushState({ zen_guard: true }, "");
    }
    const onPopState = () => {
      // Kullanıcı geri bastı → yeniden koruma kaydı ekle, dashboard'a dön
      window.history.pushState({ zen_guard: true }, "");
      _setTab("dashboard");
      setSubTab(null);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [user]); // eslint-disable-line

  // Bayi guard: izinsiz sekme geçişlerini engelle
  const setTab = (next) => {
    if (user?.role === "dealer" && !DEALER_ALLOWED_TABS.includes(next)) {
      toast.error("Bu bölüme erişim yetkiniz yok");
      return;
    }
    _setTab(next);
  };
  useEffect(() => {
    if (user?.role === "dealer" && !DEALER_ALLOWED_TABS.includes(tab)) _setTab("orders");
  }, [user]); // eslint-disable-line

  // ── Tema ────────────────────────────────────────────────────────────────────
  const [zenThemeId, setZenThemeIdState]         = useState(() => getStoredActiveThemeId());
  const [customThemes, setCustomThemes]           = useState(() => getCustomThemes());
  const [animationsEnabled, setAnimationsEnabledState] = useState(() => getStoredAnimationsEnabled());
  const [cursorId, setCursorIdState]             = useState(() => getStoredCursorId());
  const [clockStyle, setClockStyleState]         = useState(() => getStoredClockStyle());

  const t  = translations[lang];
  const tr = useMemo(() => makeTr(lang), [lang]);
  applyStatusLang(lang);

  const [moneyRates, setMoneyRates] = useState({ eurToTl: 0, usdToTl: 0 });
  const ratesState                  = useRatesProvider(!!user);
  const stockSettings               = useStockSettingsProvider(!!user);
  useEffect(() => { if (ratesState.rates) setMoneyRates(ratesState.rates); }, [ratesState.rates]);
  setMoneyContext(lang, moneyRates);

  const allThemes    = useMemo(() => [...BUILTIN_THEMES, ...customThemes], [customThemes]);
  const activeTheme  = useMemo(() => allThemes.find((th) => th.id === zenThemeId) || BUILTIN_THEMES[0], [allThemes, zenThemeId]);

  const setZenThemeId = (id) => {
    setZenThemeIdState(id);
    setStoredActiveThemeId(id);
    const chosen = allThemes.find((th) => th.id === id);
    if (chosen?.mode && chosen.mode !== "system") setTheme(chosen.mode);
  };
  const setAnimationsEnabled = (val) => { setAnimationsEnabledState(val); setStoredAnimationsEnabled(val); };
  const setCursorId          = (id)  => { setCursorIdState(id);  setStoredCursorId(id); };
  const setClockStyle        = (id)  => { setClockStyleState(id); setStoredClockStyle(id); };
  const saveCustomTheme = (themeObj) => {
    const next = [...customThemes, themeObj];
    setCustomThemes(next); persistCustomThemes(next);
    setZenThemeIdState(themeObj.id); setStoredActiveThemeId(themeObj.id);
    if (themeObj.mode) setTheme(themeObj.mode);
  };
  const deleteCustomTheme = (id) => {
    const next = customThemes.filter((th) => th.id !== id);
    setCustomThemes(next); persistCustomThemes(next);
    if (zenThemeId === id) setZenThemeId("orijinal");
  };

  // ── Side Effects ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("zen_theme", theme);
  }, [theme]);
  useEffect(() => { localStorage.setItem("zen_lang", lang); }, [lang]);
  useEffect(() => {
    const html = document.documentElement;
    Array.from(html.classList).filter((c) => c.startsWith("zen-cursor-")).forEach((c) => html.classList.remove(c));
    if (cursorId !== "default") html.classList.add(`zen-cursor-${cursorId}`);
  }, [cursorId]);
  useEffect(() => { document.documentElement.classList.toggle("zen-motion-off", !animationsEnabled); }, [animationsEnabled]);

  // Tıklama halkası animasyonu
  useEffect(() => {
    if (!animationsEnabled) return;
    const onDown = (e) => {
      const el = document.createElement("span");
      el.className = "zen-click-ripple";
      el.style.left = `${e.clientX}px`;
      el.style.top  = `${e.clientY}px`;
      document.body.appendChild(el);
      el.addEventListener("animationend", () => el.remove());
      setTimeout(() => el.remove(), 800);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [animationsEnabled]);

  // Fare pozisyonu → CSS değişkeni (metalik parıltı için)
  useEffect(() => {
    let rafId = null;
    const onMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--mouse-x", `${((e.clientX / window.innerWidth)  * 100).toFixed(1)}%`);
        document.documentElement.style.setProperty("--mouse-y", `${((e.clientY / window.innerHeight) * 100).toFixed(1)}%`);
        rafId = null;
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => { window.removeEventListener("mousemove", onMove); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  // Dinamik tema CSS enjeksiyonu
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", zenThemeId);
    let el = document.getElementById("zen-dynamic-theme-style");
    if (!el) { el = document.createElement("style"); el.id = "zen-dynamic-theme-style"; document.head.appendChild(el); }
    el.textContent = generateThemeCSS(activeTheme);
  }, [zenThemeId, activeTheme]);

  // JSPM otomatik bağlantı
  useEffect(() => {
    (async () => {
      if (localStorage.getItem("zenJspmAutoConnect") !== "1") return;
      let ports = [26443, 29443, 8181, 8182, 63318, 54692, 57069];
      let secure = false;
      try {
        const { data } = await api.get("/settings", { params: { category: "jspm_config" } });
        (data || []).forEach((r) => {
          if (r.key === "ports" && r.value) {
            const parsed = r.value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n > 0 && n < 65536);
            if (parsed.length) ports = parsed;
          }
          if (r.key === "secure") secure = r.value === "true" || r.value === "1";
        });
      } catch (_) {}
      try { jspm.init(secure, ports); } catch (_) {}
    })();
  }, []);

  // Kimlik doğrulama
  useEffect(() => {
    (async () => {
      const hasToken = !!localStorage.getItem("zen_token");
      if (!hasToken) {
        if (process.env.REACT_APP_SKIP_LOGIN === "true") {
          try {
            const { data } = await api.post("/auth/auto-login");
            if (data.token) localStorage.setItem("zen_token", data.token);
            setUser(data);
            if (data?.role === "admin") api.post("/bots/auto-heal").then(({ data: h }) => {
              if (h?.telegram?.healed || h?.whatsapp?.healed) toast.info("Botlar yeni fork URL'sine otomatik bağlandı");
            }).catch(() => {});
          } catch (_) {}
        }
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        if (data?.role === "admin") api.post("/bots/auto-heal").then(({ data: h }) => {
          if (h?.telegram?.healed || h?.whatsapp?.healed) toast.info("Botlar yeni fork URL'sine otomatik bağlandı");
        }).catch(() => {});
      } catch (_) { localStorage.removeItem("zen_token"); }
      setLoading(false);
    })();
    const onUnauth = () => setUser(null);
    window.addEventListener("zen:unauth", onUnauth);
    return () => window.removeEventListener("zen:unauth", onUnauth);
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("zen_token");
    setUser(null);
    toast.success("Çıkış yapıldı");
  };

  const toggleTheme = () => {
    if (activeTheme?.pairId) {
      const pair = allThemes.find((th) => th.id === activeTheme.pairId);
      if (pair) { setZenThemeId(pair.id); return; }
    }
    setTheme(theme === "dark" ? "light" : "dark");
  };
  const toggleLang = () => setLang(lang === "tr" ? "en" : "tr");

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Yükleniyor...</div>;

  if (!user) {
    if (process.env.REACT_APP_SKIP_LOGIN === "true")
      return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Yükleniyor...</div>;
    return (<><LoginPage onLogin={setUser} /><Toaster position="top-right" richColors closeButton duration={3000} visibleToasts={3} gap={8} /></>);
  }

  return (
    <AuthCtx.Provider value={{ user, logout }}>
    <NavigationCtx.Provider value={{ activeTab: tab, setTab, subTab, setSubTab }}>
    <RatesCtx.Provider value={ratesState}>
    <UICtx.Provider value={{ theme, toggleTheme, lang, toggleLang, t, tr, zenThemeId, setZenThemeId, activeTheme, customThemes, saveCustomTheme, deleteCustomTheme, animationsEnabled, setAnimationsEnabled, cursorId, setCursorId, clockStyle, setClockStyle }}>
    <StockSettingsCtx.Provider value={stockSettings}>
      <ThemeBackgroundFX theme={activeTheme} enabled={animationsEnabled} />
      <div className="h-screen flex flex-col bg-slate-50 dark:bg-[#0C1220]">
        <Header sidebarOpen={sidebarOpen} />
        <div className="flex flex-1 min-h-0">
          <Sidebar tab={tab} setTab={setTab} subTab={subTab} setSubTab={setSubTab} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
          <RouterShell />
        </div>
      </div>
      {user.role !== "dealer" && <AIChatWidgetComponent onDashboard={tab === "dashboard"} />}
      {user.role !== "dealer" && <AlarmChecker />}
      {user.role !== "dealer" && <PaymentResultToast />}
      <Shortcuts />
      <Toaster position="top-right" richColors closeButton duration={3000} visibleToasts={3} gap={8} />
    </StockSettingsCtx.Provider>
    </UICtx.Provider>
    </RatesCtx.Provider>
    </NavigationCtx.Provider>
    </AuthCtx.Provider>
  );
}
